import React, { useState } from "react";
import { Card, Button, Checkbox, Utensils, FileText, CloudDownload , Download} from "../../../ui";
import { tx } from "../../ludConnect.constants";
import { statusInfo } from "../../helpers/ludConnect.helpers";
import FoodItemCard from "../../../menu/foodMenu/FoodItemCard/FoodItemCard";
import "../../../menu/foodMenu/FoodMenu.css";
import { handleExportMenuExcel } from "../../../../utils/exportExcel";
import "./MenuPortal.css";

// The portal only displays live menu content: no edit/visibility actions.
const READ_ONLY_ROLE = { canWrite: false, side: null, otherSide: null };
const READ_ONLY_MENU = { _id: "portal", lifecycle: "active" };

const MenuPortal = ({
  ar,
  menu,
  canManage,
  requests = [],
  onAddItem,
  onUpdateItem,
  onUpdateFull,
  onUpdateMulti,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const count = Object.values(selected).filter(Boolean).length;
  const total = menu.sections.reduce(
    (sum, section) => sum + section.items.length,
    0,
  );


  const allItems = menu.sections.flatMap((section) =>
    (section.items || []).map((item) => ({ ...item, sectionName: section.name })),
  );

  const downloadImage = (item) => {
    const imageUrl = item?.sourceItem?.image || item?.image;
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `${item.name || "menu-item"}.jpg`;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportMenuExcel = () => {
    handleExportMenuExcel(menu.exportSource || menu);
  };

  const resolveRequestItems = (request) => {
    const payload = request.payload || {};
    if (payload.scope === "full") return allItems;
    if (payload.scope === "multi") {
      return (payload.items || []).map((requestedItem) =>
        allItems.find((item) => String(item.id) === String(requestedItem.id)) || requestedItem,
      );
    }
    const requestedId = payload.itemId || payload.item?.id;
    const existing = allItems.find((item) => String(item.id) === String(requestedId));
    return [existing || payload.item || {
      id: request.id,
      name: payload.itemName || tx("صنف", "Item", ar),
      desc: payload.comment || "",
      price: payload.item?.price || 0,
    }];
  };

  const itemRequest = (itemId) =>
    requests
      .filter(
        (request) =>
          request.payload.itemId === itemId ||
          request.payload.items?.some((item) => item.id === itemId),
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  return (
    <>
      <Card
        title={
          <span>
            <FileText size={16} />{" "}
            {tx("طلبات تحديث القائمة", "Menu update requests", ar)}{" "}
            <em>({requests.length})</em>
          </span>
        }
        hoverable={false}
        className="lud-connect__section-card"
      >
        {requests.length ? (
          <div className="lud-connect__menu-request-grid">
            {requests.flatMap((request) =>
              resolveRequestItems(request).map((item, requestItemIndex) => {
                const sourceItem = item.sourceItem || item;
                const cardKey = `request-${request.id}-${item.id || requestItemIndex}`;
                return (
                  <div className="lud-connect__food-card-wrap lud-connect__request-food-card" key={cardKey}>
                    <span className={`lud-connect__item-status ${request.status}`}>
                      {statusInfo(request.status, ar).label}
                    </span>
                    <FoodItemCard
                      item={sourceItem}
                      menu={READ_ONLY_MENU}
                      role={READ_ONLY_ROLE}
                      cardKey={cardKey}
                      expanded={expandedCard === cardKey}
                      onToggleExpand={() => setExpandedCard(expandedCard === cardKey ? null : cardKey)}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                    />
                    <div className="lud-connect__request-update">
                      <div className="lud-connect__request-status-line">
                        <b>{tx("حالة الطلب", "Request status", ar)}</b>
                        <span className={`lud-connect__item-status ${request.status}`}>
                          {statusInfo(request.status, ar).label}
                        </span>
                      </div>
                      <b>{tx("التحديث المطلوب", "Requested update", ar)}</b>
                      <p>{request.payload?.comment || request.payload?.item?.comment || "—"}</p>
                    </div>
                  </div>
                );
              }),
            )}
          </div>
        ) : (
          <div className="lud-connect__muted">
            {tx(
              "لا يوجد طلبات معلّقة حالياً",
              "There are no pending requests",
              ar,
            )}
          </div>
        )}
      </Card>

      {/* Toggle and export are sibling buttons (a button inside a
          role="button" element is not keyboard/screen-reader friendly). */}
      <div className="lud-connect__menu-toggle">
        <button
          type="button"
          className="lud-connect__menu-toggle-button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span>
            <Utensils size={16} aria-hidden="true" />{" "}
            {tx("المنيو الكامل", "Full menu", ar)} — {total}{" "}
            {tx("صنف", "items", ar)} {tx("بـ", "in", ar)}{" "}
            {menu.sections.length} {tx("قسم", "sections", ar)}
          </span>
          <span aria-hidden="true">{open ? "▴" : "▾"}</span>
        </button>
        <Button
          type="button"
          size="sm"
          variant="neutral"
          title={tx("تصدير الأصناف", "Export items", ar)}
          aria-label={tx("تصدير الأصناف", "Export items", ar)}
          icon={<CloudDownload size={16} />}
          onClick={exportMenuExcel}
        />
      </div>

      {open && (
        <div className="lud-connect__menu-body">
          {canManage && (
            <div className="lud-connect__menu-toolbar">
              <span>
                {tx(
                  "حدّد أكثر من صنف لإرسال طلب واحد لهم كلهم",
                  "Select more than one item to send one request for all of them",
                  ar,
                )}
              </span>
              <div>
                {count > 0 && (
                  <>
                    <Button
                      size="sm"
                      onClick={() =>
                        onUpdateMulti(
                          Object.keys(selected).filter((id) => selected[id]),
                        )
                      }
                    >
                      {tx("طلب تحديث لـ", "Update request for", ar)} {count}{" "}
                      {tx("صنف محدد", "selected items", ar)}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      appearance="soft"
                      onClick={() => setSelected({})}
                    >
                      {tx("إلغاء التحديد", "Clear selection", ar)}
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="primary"
                  appearance="filled"
                  onClick={onUpdateFull}
                >
                  {tx(
                    "طلب تحديث القائمة الكاملة",
                    "Request full menu update",
                    ar,
                  )}
                </Button>
              </div>
            </div>
          )}

          {menu.sections.map((section) => (
            <div key={section.id} className="lud-connect__menu-section">
              <div className="lud-connect__menu-section-head">
                <b>
                  {section.name} <span>({section.items.length})</span>
                </b>
                {canManage && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => onAddItem(section.id)}
                  >
                    {tx("إضافة صنف", "Add item", ar)}
                  </Button>
                )}
              </div>
              <div className="lud-connect__items-grid">
                {section.items.length ? (
                  section.items.map((item, itemIndex) => {
                    const related = itemRequest(item.id);
                    const sourceItem = item.sourceItem || item;
                    const cardKey = `${section.id}-${item.id}`;
                    return (
                      <div className="lud-connect__food-card-wrap" key={item.id}>
                        {canManage && (
                          <div className="lud-connect__item-check">
                            <Checkbox
                              checked={!!selected[item.id]}
                              onChange={(event) =>
                                setSelected((current) => ({
                                  ...current,
                                  [item.id]: event.target.checked,
                                }))
                              }
                            />
                          </div>
                        )}
                        {related && (
                          <span className={`lud-connect__item-status ${related.status}`}>
                            {statusInfo(related.status, ar).label}
                          </span>
                        )}
                        <FoodItemCard
                          item={sourceItem}
                          menu={READ_ONLY_MENU}
                          role={READ_ONLY_ROLE}
                          cardKey={cardKey}
                          expanded={expandedCard === cardKey}
                          onToggleExpand={() => setExpandedCard(expandedCard === cardKey ? null : cardKey)}
                          selectedOptions={selectedOptions}
                          setSelectedOptions={setSelectedOptions}
                        />
                        {(sourceItem.image || item.image) && (
                          <Button
                            fullWidth
                            size="sm"
                            variant="neutral"
                            icon={<Download size={14} />}
                            onClick={() => downloadImage(item)}
                          >
                            {tx("تحميل الصورة", "Download image", ar)}
                          </Button>
                        )}
                        {canManage && (
                          <Button
                            fullWidth
                            size="sm"
                            variant="primary"
                            onClick={() => onUpdateItem(item)}
                          >
                            {tx("طلب تحديث", "Request update", ar)}
                          </Button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span className="lud-connect__muted">
                    {tx("لا يوجد أصناف", "No items", ar)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default MenuPortal;
